import mammoth from 'mammoth';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import type { NormalizedEmail } from './parseEml';
import type { ConversionCategory, DetectedFile } from './detectFileCategory';
import { convertEmailToPdf } from './convertEmlToPdf';
import type { PdfExportQuality } from './pdfExportQuality';
import type { ImageFitMode } from './pdfExportQuality';
import type { PageDims, OrientationSetting, PageSizeSetting } from './pdfPageDims';
import { resolvePageDimensions } from './pdfPageDims';
import { convertHtmlFragmentToPdf } from './convertHtmlFragmentToPdf';
import { convertRasterBytesToPdf, rasterizeSvgToPng } from './convertImageToPdf';
import { convertSpreadsheetBufferToPdf } from './convertSpreadsheetToPdf';
import { convertPptxBufferToPdf } from './convertPptxToPdf';
import { convertSourceTextToPdf } from './convertCodeFileToPdf';
import { parseEml } from './parseEml';
import { parseMsgToNormalizedEmail } from './msgToNormalizedEmail';

export interface UniversalGlobalPdfSettings {
  pageSize: PageSizeSetting;
  orientation: OrientationSetting;
  quality: PdfExportQuality;
}

export interface UniversalEmailSettings {
  includeBody: boolean;
  includeHeaders: boolean;
  includeAttachmentList: boolean;
  embedAttachmentIds: string[];
}

export interface UniversalDocumentSettings {
  preserveFormatting: boolean;
  includeImages: boolean;
  keepLinks: boolean;
  margins: 'normal' | 'narrow' | 'wide';
}

export interface UniversalSpreadsheetSettings {
  sheetIndex: number;
  fitPageWidth: boolean;
  gridlines: boolean;
  repeatHeader: boolean;
}

export interface UniversalPresentationSettings {
  oneSlidePerPage: boolean;
  preserveAspectRatio: boolean;
  includeSpeakerNotes: boolean;
}

export interface UniversalImageSettings {
  fitMode: 'fitPage' | 'fitWidth' | 'original';
  centerImage: boolean;
  preserveExifOrientation: boolean;
  background: 'white' | 'transparent';
}

export interface UniversalHtmlSettings {
  includeBackgroundGraphics: boolean;
  printFriendly: boolean;
  preserveLinks: boolean;
}

export interface UniversalCodeSettings {
  syntaxHighlight: boolean;
  lineNumbers: boolean;
  wrapLongLines: boolean;
  theme: 'light' | 'dark';
}

export interface UniversalConvertBundle {
  global: UniversalGlobalPdfSettings;
  email?: UniversalEmailSettings;
  document?: UniversalDocumentSettings;
  spreadsheet?: UniversalSpreadsheetSettings;
  presentation?: UniversalPresentationSettings;
  image?: UniversalImageSettings;
  html?: UniversalHtmlSettings;
  code?: UniversalCodeSettings;
}

export interface UniversalConvertContext {
  email?: NormalizedEmail | null;
  imagePx?: { width: number; height: number };
}

function marginFactor(preset: UniversalDocumentSettings['margins'] | undefined): number {
  if (preset === 'narrow') return 0.82;
  if (preset === 'wide') return 1.18;
  return 1;
}

function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true, svg: true, svgFilters: true },
  });
}

export function naiveRtfToPlainText(rtf: string): string {
  return rtf
    .replace(/\r\n/g, '\n')
    .replace(/\{\\\*[^}]*\}/g, '')
    .replace(/\\'([0-9a-f]{2})/gi, (_, h: string) => String.fromCharCode(parseInt(h, 16)))
    .replace(/\\[a-z-]+\d* ?/gi, '')
    .replace(/[{}]/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

async function ensureEmailParsed(file: File, buffer: ArrayBuffer, category: ConversionCategory, ctx: UniversalConvertContext): Promise<NormalizedEmail> {
  if (ctx.email && !ctx.email.isParseError) return ctx.email;
  const id = crypto.randomUUID();
  if (category === 'email') {
    const lower = file.name.toLowerCase();
    if (lower.endsWith('.msg')) {
      return parseMsgToNormalizedEmail(buffer, id, file.name);
    }
    return parseEml(file, id);
  }
  throw new Error('Email parse missing');
}

export async function convertUniversalToPdf(
  file: File,
  buffer: ArrayBuffer,
  detected: DetectedFile,
  bundle: UniversalConvertBundle,
  ctx: UniversalConvertContext,
): Promise<Uint8Array> {
  const { category } = detected;
  const g = bundle.global;

  const imageHint =
    category === 'image' && ctx.imagePx
      ? { kind: 'image' as const, widthPx: ctx.imagePx.width, heightPx: ctx.imagePx.height }
      : { kind: 'document' as const };

  const dims: PageDims = resolvePageDimensions(g.pageSize, g.orientation, imageHint);

  const docSettings = bundle.document;
  const marginPresetFactor = marginFactor(docSettings?.margins);

  const htmlOptsBase = {
    quality: g.quality,
    pageDims: dims,
    imageFit: (bundle.image?.fitMode ?? 'fitPage') as ImageFitMode,
    marginPresetFactor,
  };

  switch (category) {
    case 'email': {
      const email = await ensureEmailParsed(file, buffer, category, ctx);
      if (email.isParseError) throw new Error(email.errorMessage || 'Could not parse email');
      const es = bundle.email!;
      return convertEmailToPdf(email, {
        includeEmailContent: es.includeBody,
        showAttachmentList: es.includeAttachmentList,
        embedAttachmentIds: Array.from(es.embedAttachmentIds),
        quality: g.quality,
        imageFit: (bundle.image?.fitMode ?? 'fitPage') as ImageFitMode,
        useRasterHtmlFallback: false,
        pageWidthPt: dims.widthPt,
        pageHeightPt: dims.heightPt,
        includeHeaders: es.includeHeaders,
      });
    }
    case 'document': {
      const ext = detected.extension.toLowerCase();
      const ds = bundle.document!;
      if (ext === 'docx') {
        const { value } = await mammoth.convertToHtml(
          { arrayBuffer: buffer },
          {
            ignoreEmptyParagraphs: true,
            convertImage: ds.includeImages
              ? mammoth.images.imgElement(async (image) => ({
                  src: `data:${image.contentType};base64,${await image.readAsBase64String()}`,
                }))
              : mammoth.images.imgElement(async () => ({ src: '' })),
          },
        );
        let html = value;
        if (!ds.keepLinks) {
          html = html.replace(/<a\b[^>]*>([\s\S]*?)<\/a>/gi, '$1');
        }
        const safe = sanitizeHtml(html);
        const title = file.name.replace(/\.[^/.]+$/, '');
        return convertHtmlFragmentToPdf(safe, {
          ...htmlOptsBase,
          title,
          useRasterHtmlFallback: false,
          marginPresetFactor,
        });
      }
      if (ext === 'rtf') {
        const txt = naiveRtfToPlainText(new TextDecoder('latin1', { fatal: false }).decode(new Uint8Array(buffer)));
        const escaped = txt
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
        const html = `<pre style="white-space:pre-wrap;font-family:system-ui">${escaped}</pre>`;
        return convertHtmlFragmentToPdf(sanitizeHtml(html), {
          ...htmlOptsBase,
          title: file.name.replace(/\.[^/.]+$/, ''),
          useRasterHtmlFallback: false,
          marginPresetFactor,
        });
      }
      throw new Error('Unsupported document format');
    }
    case 'spreadsheet': {
      const ss = bundle.spreadsheet!;
      return convertSpreadsheetBufferToPdf(buffer, {
        quality: g.quality,
        pageDims: dims,
        sheetIndex: ss.sheetIndex,
        fitPageWidth: ss.fitPageWidth,
        gridlines: ss.gridlines,
        repeatHeader: ss.repeatHeader,
      });
    }
    case 'presentation': {
      const pr = bundle.presentation!;
      return convertPptxBufferToPdf(buffer, {
        quality: g.quality,
        pageDims: dims,
        oneSlidePerPage: pr.oneSlidePerPage,
        preserveAspectRatio: pr.preserveAspectRatio,
        includeSpeakerNotes: pr.includeSpeakerNotes,
      });
    }
    case 'image': {
      const im = bundle.image!;
      const lower = file.name.toLowerCase();
      let bytes = new Uint8Array(buffer);
      let mime = file.type || 'application/octet-stream';

      if (lower.endsWith('.heic') || lower.endsWith('.heif') || mime.includes('heic')) {
        const heic2any = (await import('heic2any')).default;
        const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
        const outBlob = Array.isArray(blob) ? blob[0]! : blob;
        bytes = new Uint8Array(await outBlob.arrayBuffer());
        mime = 'image/jpeg';
      }

      if (lower.endsWith('.svg') || mime.includes('svg')) {
        const svgText = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
        bytes = new Uint8Array(await rasterizeSvgToPng(svgText));
        mime = 'image/png';
      }

      return convertRasterBytesToPdf(bytes, mime, {
        quality: g.quality,
        pageDims: dims,
        fitMode: im.fitMode,
        centerImage: im.centerImage,
        preserveExifOrientation: im.preserveExifOrientation,
        background: im.background,
      });
    }
    case 'html': {
      const hs = bundle.html!;
      const rawHtml = new TextDecoder('utf-8', { fatal: false }).decode(new Uint8Array(buffer));
      let html = rawHtml;
      if (hs.printFriendly) {
        html = html.replace(/<style[\s\S]*?<\/style>/gi, '');
      }
      if (!hs.preserveLinks) {
        html = html.replace(/<a\b[^>]*>([\s\S]*?)<\/a>/gi, '$1');
      }
      const fragment = html.includes('<body')
        ? html.replace(/^[\s\S]*<body[^>]*>/i, '').replace(/<\/body>[\s\S]*$/i, '')
        : html;
      return convertHtmlFragmentToPdf(sanitizeHtml(fragment), {
        ...htmlOptsBase,
        title: file.name.replace(/\.[^/.]+$/, ''),
        useRasterHtmlFallback: hs.includeBackgroundGraphics,
        marginPresetFactor,
      });
    }
    case 'code': {
      const cs = bundle.code!;
      const source = new TextDecoder('utf-8', { fatal: false }).decode(new Uint8Array(buffer));
      const ext = detected.extension.toLowerCase();
      return convertSourceTextToPdf(source, {
        quality: g.quality,
        pageDims: dims,
        languageExtension: ext,
        theme: cs.theme,
        lineNumbers: cs.lineNumbers,
        wrapLongLines: cs.wrapLongLines,
        syntaxHighlight: cs.syntaxHighlight,
      });
    }
    case 'text': {
      const ext = detected.extension.toLowerCase();
      const raw = new TextDecoder('utf-8', { fatal: false }).decode(new Uint8Array(buffer));
      if (ext === 'md' || ext === 'markdown') {
        const html = await marked.parse(raw, { async: true });
        const safe = sanitizeHtml(String(html));
        return convertHtmlFragmentToPdf(safe, {
          ...htmlOptsBase,
          title: file.name.replace(/\.[^/.]+$/, ''),
          useRasterHtmlFallback: false,
          marginPresetFactor,
        });
      }
      const escaped = raw
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      const wrapped = `<pre style="white-space:pre-wrap;font-family:system-ui,monospace">${escaped}</pre>`;
      return convertHtmlFragmentToPdf(sanitizeHtml(wrapped), {
        ...htmlOptsBase,
        title: file.name.replace(/\.[^/.]+$/, ''),
        useRasterHtmlFallback: false,
        marginPresetFactor: docSettings ? marginFactor(docSettings.margins) : 1,
      });
    }
    default:
      throw new Error('Unsupported file category');
  }
}
